import {isString, isNil, get, memoize} from "lodash";
import {isFiniteNumber} from "./MathUtils";
import {isDarkMode} from "./DOMUtils";
import {contrastRatio} from "wcag-contrast-utils";

export function colorHexToRGB(hex) {
    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
          }
        : null;
}

export function hexToHSL(hex) {
    hex = hex.replace("#", "");
    if (hex.length === 3) {
        hex = hex
            .split("")
            .map((c) => c + c)
            .join("");
    }
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b),
        min = Math.min(r, g, b);
    let h,
        s,
        l = (max + min) / 2;

    if (max === min) {
        h = s = 0;
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
            default:
                break;
        }
        h /= 6;
    }

    return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100),
    };
}

export function rgbToHex(r, g, b) {
    return (
        "#" +
        [r, g, b]
            .map((x) => {
                const hex = x.toString(16);
                return hex.length === 1 ? "0" + hex : hex;
            })
            .join("")
    );
}

export function hexStringToNumeric(hexString) {
    let number = 0;
    try {
        const string = hexString.replace("#", "");
        number = parseInt(string, 16);
    } catch (er) {
        console.error(er);
    }
    return isFiniteNumber(number) ? number : 0;
}

export function numericToHexString(numericValue) {
    const r = (numericValue & 0xff0000) >> 16,
        g = (numericValue & 0x00ff00) >> 8,
        b = numericValue & 0x0000ff;
    return rgbToHex(r, g, b);
}

export function getColorForPercentage(percentage = 0) {
    // slupki na zielono od 70% pomaranczowy od 30 do 70% czerwony do 30%
    let fillColor = "error";
    if (percentage >= 30) {
        fillColor = "warning";
    }
    // if (percentage >= 40) {
    //     fillColor = "info";
    // }
    if (percentage >= 70) {
        fillColor = "success";
    }
    return fillColor;
}

export const colors = {
    success: "#4eb848",
    primary: "#4eb848",
    secondary: "#4C4C4E",
    error: "#B90E0E",
    info: "#2196f3",
    warning: "#ffc107",
    white: "#ffffff",
    colorLightText: "#ffffff",
    colorDarkText: "#000000",
    red: "#B90E0E",
    birth: "#754D88",
    mating: "#4D884D",
    sows: "#E19A21",
    piglets: "#CE5993",
    other: "#753F31",
    porkers: "#01af7e",
};

export const colorsNonInvasive = {
    teal: "#009688",
    pink: "#e91e63",
    blue: "#2196f3",
    orange: "#ffc107",
    cyan: "#00bcd4",
    yellow: "#ffeb3b",
    green: "#4eb848",
    deeporange: "#ff5722",
    purple: "#9c27b0",
};

export const diodeColors = [
    "#00FF00",
    "#00FFFF",
    "#FFFF00",
    "#FF00FF",
    "#FFC000",
    "#FF0000",
    "#fcf7a5",
    "#0000FF",
    "#C8FF00",
    "#2196f3",
    "#9c00ff",
    "#6DAEB0",
];

/**
 * Paleta kategorii do wykresów i oznaczeń grup (np. reguły grupowania w tabeli i ich histogram).
 * Kolejność stała - kolor należy do kategorii, nie do jej pozycji po filtrowaniu; nie zapętlamy:
 * kategorie ponad paletę łączy się w "Pozostałe" (szary `neutral`).
 * Sprawdzona na tłach background50 (jasne #f6f6f6, ciemne #2d2d2d): sąsiednie barwy rozróżnialne także
 * przy daltonizmie. Kilka barw ma kontrast < 3:1 z tłem - kolor zawsze stoi obok etykiety (legenda, podpis).
 * Ciemny motyw ma własne odcienie tych samych barw.
 */
export const categoricalColors = {
    light: [
        "#2a78d6",
        "#eb6834",
        "#1baf7a",
        "#eda100",
        "#e87ba4",
        "#008300",
        "#6250d6",
        "#e34948",
    ],
    dark: [
        "#3987e5",
        "#d95926",
        "#199e70",
        "#c98500",
        "#d55181",
        "#008300",
        "#9085e9",
        "#e66767",
    ],
    // "Pozostałe" / brak kategorii - kontrast >= 3:1 z tłem w obu motywach
    neutral: {light: "#7c7c7c", dark: "#9e9e9e"},
};

/**
 * Kolor kategorii o danym indeksie.
 * @param {number} index pozycja kategorii (od 0)
 * @param {boolean} [dark] ciemny motyw
 * @returns {string|null} null - poza paletą (kategoria idzie do "Pozostałe")
 */
export function getCategoricalColor(index, dark = isDarkMode()) {
    return categoricalColors[dark ? "dark" : "light"][index] ?? null;
}

/**
 * Szary dla "Pozostałe" i braku wartości.
 * @param {boolean} [dark] ciemny motyw
 * @returns {string}
 */
export function getCategoricalNeutral(dark = isDarkMode()) {
    return categoricalColors.neutral[dark ? "dark" : "light"];
}

export const themeDependentColors = {
    dark: {
        background: "#1e1e1e",
        background50: "#2d2d2d",
        background100: "#2a2a2a",
        background200: "#272727",
        background300: "#242424",
        background400: "#212121",
        background500: "#1e1e1e",
        background600: "#1b1b1b",
        background700: "#181818",
        background800: "#151515",
        background900: "#121212",
        disabled: "#c8c8c8",
        map: {
            forestBG: "#80e27e",
            forestFG: "#4caf50",
        },
        fontColor: "#DCDDDE",
    },
    light: {
        background: "#a5a5a5",
        background50: "#f6f6f6",
        background100: "#f1f1f1",
        background200: "#f1f1f1",
        background300: "#e6e6e6",
        background400: "#c4c4c4",
        background500: "#a5a5a5",
        background600: "#7c7c7c",
        background700: "#676767",
        background800: "#484848",
        background900: "#262626",
        disabled: "#aaaaaa",
        map: {
            forestBG: "#80e27e",
            forestFG: "#4caf50",
        },
        fontColor: "#000000",
    },
};
export const defaultSwatchesColors = [
    ["#b71c1c", "#d32f2f", "#f44336", "#e57373", "#ffcdd2"],
    ["#880e4f", "#c2185b", "#e91e63", "#f06292", "#f8bbd0"],
    ["#4a148c", "#7b1fa2", "#9c27b0", "#ba68c8", "#e1bee7"],
    ["#311b92", "#512da8", "#673ab7", "#9575cd", "#d1c4e9"],
    ["#1a237e", "#303f9f", "#3f51b5", "#7986cb", "#c5cae9"],
    ["#0d47a1", "#1976d2", "#2196f3", "#64b5f6", "#bbdefb"],
    ["#01579b", "#0288d1", "#03a9f4", "#4fc3f7", "#b3e5fc"],
    ["#006064", "#0097a7", "#00bcd4", "#4dd0e1", "#b2ebf2"],
    ["#004d40", "#00796b", "#009688", "#4db6ac", "#b2dfdb"],
    ["#194D33", "#388e3c", "#4caf50", "#81c784", "#c8e6c9"],
    ["#33691e", "#689f38", "#8bc34a", "#aed581", "#dcedc8"],
    ["#827717", "#afb42b", "#cddc39", "#dce775", "#f0f4c3"],
    ["#f57f17", "#fbc02d", "#ffeb3b", "#fff176", "#fff9c4"],
    ["#ff6f00", "#ffa000", "#ffc107", "#ffd54f", "#ffecb3"],
    ["#e65100", "#f57c00", "#ff9800", "#ffb74d", "#ffe0b2"],
    ["#bf360c", "#e64a19", "#ff5722", "#ff8a65", "#ffccbc"],
    ["#3e2723", "#5d4037", "#795548", "#a1887f", "#d7ccc8"],
    ["#263238", "#455a64", "#607d8b", "#90a4ae", "#cfd8dc"],
    ["#000000", "#525252", "#969696", "#D9D9D9", "#FFFFFF"],
];

export const deleteSimilarColors = (colors, data = defaultSwatchesColors) => {
    const accuracy = 0.2;

    for (let i = 0; i < colors.length; i++) {
        const color = colors[i];
        const [h1, s1, l1] = hexToHsl(color);

        const filteredArray = data.map((pivotArray) => {
            const filteredColors = pivotArray.filter((k) => {
                const [h2, s2, l2] = hexToHsl(k);

                const hue = Math.abs(h1 - h2);
                const saturation = Math.abs(s1 - s2);
                const lightness = Math.abs(l1 - l2);

                if (
                    hue <= accuracy &&
                    saturation <= accuracy &&
                    lightness <= accuracy
                ) {
                    return false;
                }

                return true;
            });

            return filteredColors;
        });

        data = filteredArray.filter((item) => item.length > 0);
    }

    return data;
};
export const hexToHsl = (hexColor) => {
    const r = parseInt(hexColor.slice(1, 3), 16) / 255;
    const g = parseInt(hexColor.slice(3, 5), 16) / 255;
    const b = parseInt(hexColor.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l;

    l = (max + min) / 2;

    if (max === min) {
        h = s = 0;
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
            default:
                break;
        }

        h /= 6;
    }

    return [h, s, l];
};

export function getColorByName(color = "primary") {
    if (color === "currentColor") return color;
    return color.startsWith("#")
        ? color
        : get(
              {
                  ...colors,
                  ...colorsNonInvasive,
                  ...themeDependentColors[isDarkMode() ? "dark" : "light"],
              },
              color,
              "#000000"
          );
}

export function getColorByIndex(index = Math.floor(Math.random() * 1000)) {
    return colorsNonInvasive[
        Object.keys(colorsNonInvasive)[
            index % Object.keys(colorsNonInvasive).length
        ]
    ];
}

function pickHex(color1, color2, weight) {
    let p = weight;
    let w = p * 2 - 1;
    let w1 = (w / 1 + 1) / 2;
    let w2 = 1 - w1;
    let rgb = [
        Math.round(color1[0] * w1 + color2[0] * w2),
        Math.round(color1[1] * w1 + color2[1] * w2),
        Math.round(color1[2] * w1 + color2[2] * w2),
    ];
    return rgb;
}

export function getColorInGradient(
    percentage = 0,
    gradientDefinition = [
        {percent: 0, color: getColorByName("error")},
        {percent: 40, color: getColorByName("warning")},
        {percent: 75, color: getColorByName("info")},
        {percent: 100, color: getColorByName("success")},
    ]
) {
    let gradient;
    percentage = Math.round(percentage);
    percentage = Math.min(percentage, 100);
    percentage = Math.max(percentage, 0);
    if (gradientDefinition.length < 2)
        throw new Error("Gradient must be made of 2 or more colors");
    gradient = gradientDefinition.map((point) => {
        let percent = point.percent;
        let rgb = colorHexToRGB(point.color);
        rgb = rgb ? [rgb.r, rgb.g, rgb.b] : [0, 0, 0];
        return [percent, rgb];
    });
    let colorRange = [0, 1];
    if (percentage !== 0) {
        for (let index = 1; index < gradient.length; index++) {
            if (percentage <= gradient[index][0]) {
                colorRange = [index - 1, index];
                break;
            }
        }
    }
    //console.log("colorRange", colorRange);
    let firstcolor = gradient[colorRange[0]][1];
    let secondcolor = gradient[colorRange[1]][1];
    //console.log(firstcolor, "SECOND", secondcolor);

    let firstcolor_x = 100 * (gradient[colorRange[0]][0] / 100);
    let secondcolor_x = 100 * (gradient[colorRange[1]][0] / 100) - firstcolor_x;
    let slider_x = 100 * (percentage / 100) - firstcolor_x;
    let ratio = slider_x / secondcolor_x;

    let result = pickHex(secondcolor, firstcolor, ratio);

    return "rgb(" + result.join() + ")";
}

export function getColorByStringToIndex(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let c = hash & 0x00ffffff;
    return getColorByIndex(c);
}

export function getColorByString(str, {appendHash = false} = {}) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let c = (hash & 0x00ffffff).toString(16).toUpperCase();
    const value = "00000".substring(0, 6 - c.length) + c;
    return appendHash ? `#${value}` : value;
}

export function brightness(color) {
    if (isString(color)) {
        if (color.startsWith("rgb")) {
            color = color
                .split("(")[1]
                .split(")")[0]
                .split(",")
                .map((o) => +o || 0);
        } else {
            color = colorHexToRGB(color);
            color = color ? [color.r, color.g, color.b] : [0, 0, 0];
        }
    } else {
        color = color ? [color.r, color.g, color.b] : [0, 0, 0];
    }
    return Math.round(
        color[0] * 299 + color[1] * 587 + (color[2] * 114) / 1000
    );
}

export function textContrast(
    color,
    light = getColorByName("colorLightText"),
    dark = getColorByName("colorDarkText")
) {
    if (isNil(color)) {
        return color;
    } else {
        let colorBrightness = brightness(color);
        let lightTextBrightness = brightness(light);
        // let darkTextBrightness = brightness(dark);
        return Math.abs(colorBrightness - 30000) < lightTextBrightness / 2
            ? light
            : dark;
    }
}

export function setBackgroundColorInMeta() {
    setTimeout(() => {
        let root = document.getElementById("root");
        let css = getComputedStyle(root).getPropertyValue("background-color");
        let rgbArray = css
            .split("(")[1]
            .split(")")[0]
            .split(",")
            .map((item) => +item);
        let hex = rgbToHex(...rgbArray);
        document
            .querySelector('meta[name="theme-color"]')
            .setAttribute("content", hex);
    });
}

function lightenDarkenColor(color, amount) {
    const numeric = hexStringToNumeric(color);
    const lighten = (value, amount) => {
        const newValue = value + amount;
        return newValue > 255 ? 255 : newValue < 0 ? 0 : Math.round(newValue);
    };
    return rgbToHex(
        lighten(numeric >> 16, amount),
        lighten((numeric >> 8) & 0x00ff, amount),
        lighten(numeric & 0x0000ff, amount)
    );
}

export function darken(color, amount) {
    return lightenDarkenColor(color, -amount);
}

export function lighten(color, amount) {
    return lightenDarkenColor(color, amount);
}

export function getColorMobileResult(previous, current, revert) {
    if (current > previous) return revert ? "error" : "success";
    else if (current < previous) return revert ? "success" : "error";
    return null;
}

export const getColorByHashedKey = (key) => {
    const colors = deleteSimilarColors([
        themeDependentColors.dark.background50,
        themeDependentColors.light.background50,
    ]);
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
        hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }

    const arrayIndex = Math.abs(hash) % colors.length;
    const colorIndex = Math.abs(hash) % colors[arrayIndex].length;

    return colors[arrayIndex][colorIndex];
};

export function getHeatmapColor(
    value,
    min,
    max,
    fromColor = getColorByName("success"),
    toColor = getColorByName("error")
) {
    if (!isFiniteNumber(value) || !isFiniteNumber(min) || !isFiniteNumber(max))
        return fromColor;

    if (min === max) return toColor;

    // Normalize to 0–100 percentage scale
    const clamped = Math.max(min, Math.min(max, value));
    const percentage = ((clamped - min) / (max - min)) * 100;

    return getColorInGradient(percentage, [
        {percent: 0, color: fromColor},
        {percent: 100, color: toColor},
    ]);
}

export function applyOpacityToColor(color, alpha = 1) {
    alpha = Math.max(0, Math.min(1, alpha)); // Clamp alpha between 0 and 1

    // Check if color is already rgba
    if (color.startsWith("rgba")) {
        const parts = color.match(
            /rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]*)\)/
        );
        if (parts) {
            const [, r, g, b] = parts;
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
    }

    // Handle rgb without alpha
    if (color.startsWith("rgb")) {
        const parts = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (parts) {
            const [, r, g, b] = parts;
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
    }

    // Handle hex
    if (color.startsWith("#")) {
        let r, g, b;

        if (color.length === 4) {
            // Short hex #rgb
            r = parseInt(color[1] + color[1], 16);
            g = parseInt(color[2] + color[2], 16);
            b = parseInt(color[3] + color[3], 16);
        } else if (color.length === 7) {
            // Full hex #rrggbb
            r = parseInt(color.slice(1, 3), 16);
            g = parseInt(color.slice(3, 5), 16);
            b = parseInt(color.slice(5, 7), 16);
        } else {
            throw new Error("Invalid hex color");
        }

        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    // Fallback
    return color;
}

export function colorToSVGFillAndOpacity(color) {
    const result = {fill: color, fillOpacity: 1};

    // Check for rgba format
    const rgbaMatch = color.match(
        /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([0-9.]+))?\)$/i
    );
    if (rgbaMatch) {
        const [, r, g, b, a] = rgbaMatch;
        result.fill = `rgb(${r}, ${g}, ${b})`;
        if (typeof a !== "undefined") {
            result.fillOpacity = parseFloat(a);
        }
    }

    return result;
}

/** Normalize to #rrggbb, expand #rgb, uppercase. */
function normalizeHex(hex) {
    let h = hex.trim();
    if (!h.startsWith("#")) h = "#" + h;
    if (h.length === 4) {
        h = "#" + h[1] + h[1] + h[2] + h[2] + h[3] + h[3];
    }
    return h.toUpperCase();
}

/**
 * Adjusts ONLY the foreground color until the WCAG contrast ratio meets target.
 *
 * @param {string} fg - starting foreground color (#rgb or #rrggbb)
 * @param {string} bg - background color (#rgb or #rrggbb) – unchanged
 * @param {number} [targetRatio=4.5] - WCAG ratio (e.g. 4.5 for AA, 7 for AAA)
 * @param {number} [step=6] - per-iteration channel delta (0–255)
 * @param {number} [maxSteps=60] - max iterations before giving up
 * @returns {string}
 */
function _fixForegroundContrast(
    fg,
    bg,
    targetRatio = 4.5,
    step = 3,
    maxSteps = 90
) {
    fg = normalizeHex(fg);
    bg = normalizeHex(bg);

    let currentRatio = contrastRatio(fg, bg);
    if (currentRatio >= targetRatio) {
        // even if it passes, apply ink-floor check below on light bg
    } else {
        // --- WCAG pass search (foreground only) ---
        const probeLight = contrastRatio(lighten(fg, step), bg);
        const probeDark = contrastRatio(darken(fg, step), bg);
        const useLighten = probeLight > probeDark;

        let working = fg;
        let best = fg;
        let bestRatio = currentRatio;

        for (let i = 0; i < maxSteps; i++) {
            working = useLighten
                ? lighten(working, step)
                : darken(working, step);
            const r = contrastRatio(working, bg);

            if (r > bestRatio) {
                bestRatio = r;
                best = working;
                if (r >= targetRatio) {
                    fg = working;
                    currentRatio = r;
                    break;
                }
            }

            const [, , l] = hexToHsl(working);
            if ((useLighten && l >= 0.9) || (!useLighten && l <= 0.55)) {
                // hit gamut end → keep best so far
                fg = best;
                currentRatio = bestRatio;
                break;
            }
        }

        // if loop didn’t set fg (didn’t reach target or break), adopt best
        if (currentRatio < targetRatio) {
            fg = best;
        }
    }
    return fg;
}

export const fixForegroundContrastWithWCAG = memoize(
    _fixForegroundContrast,
    (...args) => args.join("+")
);

export const resolveColorHexByNameWithWCAG = (color) => {
    if (color.startsWith("#")) {
        return fixForegroundContrastWithWCAG(
            color,
            getColorByName("background50")
        );
    }
    return getColorByName(color);
};
